'use client';

import type { KeyboardEvent, ReactNode } from 'react';
import { Text } from '../../../../../components/styled/text';

type InlineObjectReferenceLinkProps = {
  icon?: ReactNode;
  iconColor?: string;
  name: string;
  prefix?: string;
  onClick?: () => void;
  title?: string;
};

export function InlineObjectReferenceLink({
  icon,
  iconColor,
  name,
  prefix,
  onClick,
  title,
}: InlineObjectReferenceLinkProps) {
  const displayTitle = title ?? name;
  const isClickable = Boolean(onClick);

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (!onClick) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onClick();
  };

  return (
    <Text
      as="span"
      display="inline-flex"
      alignItems="center"
      gap={1}
      fontSize="xs"
      lineHeight={1.5}
      flexWrap="wrap"
    >
      {prefix ? (
        <Text as="span" color="fg.muted">
          {prefix}{' '}
        </Text>
      ) : null}
      {isClickable ? (
        <Text
          as="span"
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          px={1.5}
          borderRadius="sm"
          bg="terra.a1"
          border="1px solid"
          borderColor="terra.a3"
          fontSize="xs"
          fontWeight="medium"
          color="terra.11"
          maxWidth="260px"
          verticalAlign="middle"
          cursor="pointer"
          title={displayTitle}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          _hover={{ bg: 'terra.a2', borderColor: 'terra.a4' }}
        >
          {icon ? (
            <Text
              as="span"
              color={iconColor}
              display="inline-flex"
              flexShrink={0}
            >
              {icon}
            </Text>
          ) : null}
          <Text
            as="span"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            minWidth={0}
          >
            {name}
          </Text>
        </Text>
      ) : (
        <Text
          as="span"
          color="fg.muted"
          truncate
          maxWidth="260px"
          title={displayTitle}
        >
          {name}
        </Text>
      )}
    </Text>
  );
}
